import { Link } from "react-router-dom";
import { Linkedin, Instagram } from "lucide-react";
import siteFooter from "@/content/siteFooter.json";

type FooterLink = {
  label: string;
  /** Static route or URL. Ignored when `action` is set. */
  href?: string;
  internal?: boolean;
  external?: boolean;
  /**
   * Action token instead of an href. Lets the footer trigger global
   * behaviour (e.g. open the demo modal) without coupling to any
   * specific page's modal state.
   */
  action?: 'book-demo';
};

// Footer index — brand block (left) + three columns : For Brands
// (B2B funnel), For Shoppers (B2C funnel), Company. garu serves two
// audiences and the footer needs to surface both routes — fashion
// brands looking to install AI sizing + try-on, and shoppers looking
// for the Chrome extension / consumer try-on. Mixing them into a
// single "Product" column hid the shopper journey entirely and made
// garu read as B2B-only. Aiuta / Stripe pattern.
//
// Deliberately curated, not exhaustive: the SEO long-tail (keyword
// landing pages, compare pages, field guides) is internally linked by
// the Explore strip on the merchant landing pages, not by this footer.
//
// The link lists live in src/content/siteFooter.json because
// scripts/generate-route-html.js renders the SAME lists into the
// pre-JS crawler snapshot of every route. Crawlers that skip
// JavaScript (Ahrefs Site Audit by default, GPTBot, CCBot) never see
// this component, and before the snapshot carried the footer, every
// page only the footer linked to (/about, /contact, /privacy, /docs…)
// was an orphan to them. Edit the JSON, not this file, to add a link.

// "Book a demo" links straight to a real scheduling page so visitors
// leave with a meeting booked, not a lead form that goes to an inbox.
const CALENDLY_URL = "https://calendly.com/claire-garu/30min";
const SHOPIFY_APP_STORE_URL =
  "https://apps.shopify.com/garu-virtual-try-on?utm_source=garu_footer&utm_medium=website&utm_campaign=shopify_launch";

type FooterJsonLink = { label: string; href: string; external?: boolean };

function toFooterLink(link: FooterJsonLink): FooterLink {
  return link.external
    ? { label: link.label, href: link.href, external: true }
    : { label: link.label, href: link.href, internal: true };
}

const footerColumns: { title: string; links: FooterLink[] }[] = siteFooter.columns.map((column) => ({
  title: column.title,
  links: column.links.map(toFooterLink),
}));

// The demo CTA is a window event, not a URL, so it can't live in the
// JSON (and a crawler snapshot has nothing to render for it). Appended
// to the brands column here only.
footerColumns[0].links.push({ label: "Book a Demo", action: "book-demo" });

const legalLinks: FooterLink[] = siteFooter.legal.map(toFooterLink);

// Social handles already attested in the Organization JSON-LD in
// index.html — surface them here too as the editorial micro-row
// under the brand block (B2B credibility via LinkedIn, B2C reach
// via Instagram + TikTok).
const socialLinks: { label: string; href: string; icon: "linkedin" | "instagram" | "tiktok" }[] = [
  { label: "LinkedIn", href: "https://www.linkedin.com/company/garuai", icon: "linkedin" },
  { label: "Instagram", href: "https://www.instagram.com/garu.ai/", icon: "instagram" },
  { label: "TikTok", href: "https://www.tiktok.com/@trygaru.ai", icon: "tiktok" },
];

function SocialIcon({ icon, className }: { icon: "linkedin" | "instagram" | "tiktok"; className?: string }) {
  if (icon === "linkedin") return <Linkedin className={className} strokeWidth={1.5} />;
  if (icon === "instagram") return <Instagram className={className} strokeWidth={1.5} />;
  // Custom TikTok glyph — lucide-react doesn't ship one. Stroke kept
  // visually weighted to match Linkedin/Instagram outline icons so
  // the social row reads as one consistent set, not three styles.
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M21 8.5a8.5 8.5 0 0 1-5-1.6V15a6 6 0 1 1-6-6c.34 0 .67.03 1 .08v3.13A3 3 0 1 0 13 15V3h3a5.5 5.5 0 0 0 5 5z" />
    </svg>
  );
}

function FooterLinkItem({ link }: { link: FooterLink }) {
  // Standard footer-link typography (14px) — the size every B2B
  // luxury / SaaS site uses for footer indexes (Swap, Stripe,
  // Linear, Aiuta). Inherits text-align from the parent column so
  // mobile-centred columns stay legible while md+ columns anchor
  // to the start. `whitespace-nowrap` stops compound labels like
  // "Virtual Try-On" or "Chrome Extension" from breaking mid-link.
  // Dark-footer link treatment (Lumoo): quiet neutral-400 at rest,
  // lifting to white on hover.
  const className =
    "text-[14px] text-neutral-400 hover:text-white transition-colors whitespace-nowrap";

  // Action item : dispatches a window-level event the host page
  // listens for via useBookDemoEvent. Rendered as a <button> so it
  // gets proper a11y semantics (and isn't an anchor with no href).
  if (link.action === "book-demo") {
    return (
      <button
        type="button"
        onClick={() => {
          window.dispatchEvent(new CustomEvent("garu:book-demo"));
        }}
        className={className}
      >
        {link.label}
      </button>
    );
  }

  if (link.internal && link.href) {
    return (
      <Link to={link.href} className={className}>
        {link.label}
      </Link>
    );
  }
  return (
    <a
      href={link.href ?? "#"}
      target={link.external ? "_blank" : undefined}
      rel={link.external ? "noopener noreferrer" : undefined}
      className={className}
    >
      {link.label}
    </a>
  );
}

function FooterColumn({
  title,
  links,
}: {
  title: string;
  links: FooterLink[];
}) {
  return (
    <div className="break-inside-avoid flex flex-col items-center md:items-start text-center md:text-left">
      <h3 className="text-sm font-semibold text-white tracking-tight mb-4">
        {title}
      </h3>
      <ul className="flex flex-col gap-3">
        {links.map((link) => (
          <li key={link.label}>
            <FooterLinkItem link={link} />
          </li>
        ))}
      </ul>
    </div>
  );
}

type FooterProps = {
  /**
   * Opt-in for the closing "Book a demo" CTA section that sits above
   * the link footer (Aiuta-style standalone screen).
   *
   * Defaults to false because the closer is the *site-level* pitch
   * close — the "you've finished reading my pitch, now commit" beat.
   * It belongs on the marketing landing surface and nowhere else.
   * Conversion pages already convert (ROI calculator, pricing,
   * contact), legal pages shouldn't sell (terms, privacy, cookies),
   * transactional pages shouldn't sell (email confirmed), and
   * self-referential pages would link back to themselves (the
   * "View interactive demo" CTA on /demo-store).
   *
   * Pass `showClosingCta` only on the public landing page(s).
   */
  showClosingCta?: boolean;
};

export const Footer = ({ showClosingCta = false }: FooterProps = {}) => {
  return (
    <>
      {/* Closing CTA : Aiuta-style standalone screen. Lives as its
          own <section> outside the <footer> so it reads as a
          dedicated "do you want to talk to us?" moment first, then
          the user scrolls and the link footer is its second screen.
          Both surfaces share bg-neutral-50 so the grey canvas is
          visually continuous — only the spacing separates them.

          Composition is headline + supporting subline + two CTAs
          (primary Shopify install + secondary demo booking). The
          subline does the *selling* — without it the section was
          beautiful but under-converting (a YC partner noted it gave
          no reason to click). The pair-of-CTAs mirrors the hero's
          two-pill cluster so the page opens and closes on the same
          two affordances : commit through the App Store or talk to
          the team before rollout.

          min-h sized so the headline + subline + CTAs are
          comfortably centred in the viewport with lots of
          breathing room above and below, mirroring Aiuta's
          footer-close pattern. */}
      {showClosingCta && (
      <section
        aria-label="Book a demo"
        className="relative w-full bg-neutral-50 min-h-[70vh] md:min-h-[78vh] flex items-center justify-center px-6 lg:px-24 py-24 md:py-32"
      >
        <div className="max-w-[1440px] w-full mx-auto text-center">
          <h2 className="font-heading text-4xl md:text-5xl lg:text-[3.5rem] font-semibold tracking-[-0.02em] leading-[1.05] text-neutral-900 max-w-[26ch] mx-auto">
            Bring real fit intelligence to your collection.
          </h2>
          {/* Supporting subline : the conversion driver. Names the
              product (AI sizing + 360° try-on), the audience
              (fashion brands), and the three buyer outcomes (fewer
              fit doubts / higher conversion / fewer size-related
              returns) without using a single unprovable number. */}
          <p className="mt-6 max-w-[58ch] mx-auto text-base md:text-lg leading-relaxed text-neutral-500">
            AI sizing and 360° try-on for fashion brands that want fewer fit doubts, higher conversion, and fewer size-related returns.
          </p>
          {/* CTA cluster : primary deep-emerald (the money button)
              + secondary white-with-hairline (browse a live demo
              store on Garu). Stacks on mobile so each pill stays
              full-width tappable; sits inline from sm+. */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-3.5">
            <a
              href={SHOPIFY_APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="garu-btn-cta inline-flex min-w-[10.5rem] items-center justify-center gap-2"
            >
              Install on Shopify
            </a>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="garu-btn-secondary inline-flex min-w-[10.5rem] items-center justify-center gap-2"
            >
              Book a demo
            </a>
          </div>
        </div>
      </section>
      )}

      {/* Footer surface : dark editorial close (Lumoo's black footer
            with SpreeAI's oversized-wordmark basement) —
            1. Brand block + link columns
            2. Legal strip
            3. Giant ghost wordmark pinned at the bottom edge */}
      <footer
        className="relative w-full flex flex-col overflow-hidden"
        style={{ backgroundColor: '#0E0E0E' }}
        role="contentinfo"
        aria-label="Site footer"
      >
      {/* Top section : brand block + three link columns
          (For Brands / For Shoppers / Company). Brand block holds the
          wordmark, the one-line product positioning, and a social row.
          Three link columns split the funnel by audience so a brand
          buyer immediately sees AI Sizing / Pricing / ROI Calculator,
          and a shopper immediately sees the Chrome extension / Explore
          / Sign in — without the two journeys colliding in a single
          "Product" list. Padding kept generous but not extreme so the
          info, the legal strip, and the line-art panorama below read
          as *one* continuous editorial canvas instead of two stacked
          sections. */}
      <div className="px-6 lg:px-16 max-w-[1440px] w-full mx-auto pt-20 md:pt-24 lg:pt-28 pb-12 md:pb-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 xl:gap-14">
          {/* Brand block — wordmark + positioning + social row */}
          <div className="lg:col-span-5 flex flex-col items-center md:items-start text-center md:text-left">
            <Link
              to="/"
              aria-label="garu home"
              className="inline-flex items-baseline"
            >
              <span
                className="text-4xl md:text-5xl font-bold tracking-[0.06em] leading-[1.15] pb-1 text-white"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                garu
              </span>
            </Link>
            {/* Positioning line (investor-grade): the layer, then the
                wedge — bigger than a try-on tool, concrete about the
                entry point. */}
            <p className="mt-5 text-[14px] md:text-[15px] leading-[1.65] text-neutral-400 max-w-[38ch]">
              The AI fitting room and fit-intelligence layer for fashion
              commerce, starting with virtual try-on and smart sizing
              for Shopify brands.
            </p>

            {/* Social row : small outline icons on a hairline ground.
                LinkedIn anchors the B2B credibility line, Instagram and
                TikTok anchor the B2C reach. Sized at 18px so they read
                as a quiet editorial set, not big SaaS social buttons. */}
            <ul className="mt-7 flex items-center gap-4">
              {socialLinks.map((s) => (
                <li key={s.label}>
                  <a
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`garu on ${s.label}`}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full text-neutral-400 hover:text-white transition-colors"
                  >
                    <SocialIcon icon={s.icon} className="h-[18px] w-[18px]" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Link columns — brands, shoppers, company */}
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-12 sm:gap-10 md:gap-14 lg:gap-10">
            {footerColumns.map((column) => (
              <FooterColumn key={column.title} title={column.title} links={column.links} />
            ))}
          </div>
        </div>
      </div>

      {/* Legal strip : Privacy + Terms + Cookies on the left, copyright
          on the right. The hairline divider that used to live above
          this strip was the loudest "two-sections" cue in the footer
          — removing it lets the info above flow into the legal line
          and on into the line-art panorama below as one canvas. The
          legal-strip text colour is already quiet enough to read as a
          natural tail to the link columns without an explicit rule
          between them. Padding tuned tight against the panorama. */}
      <div className="px-6 lg:px-16 max-w-[1440px] w-full mx-auto pb-6 md:pb-8 flex flex-col md:flex-row items-center md:items-center justify-between gap-4">
        <nav className="flex flex-wrap items-center justify-center md:justify-start gap-x-6 gap-y-2 text-[13px] text-neutral-400">
          {legalLinks.map((link) => (
            <FooterLinkItem key={link.label} link={link} />
          ))}
        </nav>
        <p className="text-[13px] text-neutral-500">
          © {new Date().getFullYear()} garu. All rights reserved.
        </p>
      </div>

      {/* 4. Ghost wordmark basement : SpreeAI's signature — the
          brand name set enormous at the very bottom edge, cropped
          by the footer's overflow-hidden so it reads as
          architecture, not a headline. The line-art panorama that
          used to close the light footer relied on
          mix-blend-multiply against a near-white ground, which
          can't survive the dark surface. */}
      <div
        aria-hidden
        className="w-full select-none pointer-events-none -mb-[0.34em]"
      >
        <p
          className="text-center font-bold leading-none"
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: 'clamp(6rem, 23vw, 21rem)',
            letterSpacing: '0.02em',
            color: 'rgba(255, 255, 255, 0.05)',
          }}
        >
          garu
        </p>
      </div>
    </footer>
    </>
  );
};
